// ── Third-party injected-code noise filter ──────────────────────────
// Browser extensions and embedded browsers (Outlook SafeLinks, CefSharp)
// inject scripts into hub pages that patch `appendChild`, the `Error`
// constructor, and `JSON.stringify` over live DOM nodes. The exceptions
// they throw look like ours — they carry no useful stack into our code —
// yet they flood Sentry as if they were hub bugs. Each rule below matches
// a specific, provably-third-party signature so genuine app errors are
// never suppressed.
//
// 1. ARTIC-HUB-15S — `Converting circular structure to JSON` whose message
//    also mentions `__reactFiber$…`. Third-party code stringifies one of
//    our DOM nodes and hits React's internal fiber back-reference. We
//    require BOTH substrings: a genuine circular-JSON bug in OUR code (e.g.
//    a self-referential object passed to JSON.stringify) would NOT mention
//    a react fiber, and must still reach Sentry. Never blanket-ignore
//    circular-JSON on the message alone.
//
// 2. ARTIC-HUB-15B — `Object Not Found Matching Id:…, MethodName:…,
//    ParamCount:…`. This is the CefSharp / Outlook host-object bridge
//    signature; it originates entirely inside the embedding browser's
//    injected shim and is never produced by hub code.
//
// 3. ARTIC-HUB-15T — `An event processor returned \`null\`, will not send
//    event.`. This is the Sentry SDK's own internal debug log; a patched
//    `Error` constructor promotes it into a thrown Error that re-enters
//    Sentry. It is never actionable.

const REACT_FIBER_MARKER = '__reactFiber$';
const CIRCULAR_JSON_MARKER = 'Converting circular structure to JSON';
const CEFSHARP_MARKER = 'Object Not Found Matching Id';
const EVENT_PROCESSOR_MARKER = 'An event processor returned';

/**
 * Returns true when an error message matches a known third-party
 * injected-code signature that should be dropped before reaching Sentry.
 * Pure and side-effect free so it can be unit-tested in isolation.
 */
export function shouldIgnoreError(message: string): boolean {
  // ARTIC-HUB-15S: circular-JSON only when it also implicates a react fiber.
  if (message.includes(CIRCULAR_JSON_MARKER) && message.includes(REACT_FIBER_MARKER)) {
    return true;
  }
  // ARTIC-HUB-15B: CefSharp / Outlook host-object bridge.
  if (message.includes(CEFSHARP_MARKER)) {
    return true;
  }
  // ARTIC-HUB-15T: Sentry SDK internal log surfaced through a patched Error.
  if (message.includes(EVENT_PROCESSOR_MARKER)) {
    return true;
  }
  return false;
}
